import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { ZegoStreamService } from '../../../../services/zego-stream.service';
import { getUserInfo, isDevice } from '../../../../utils/utils';
import { AppIconComponent } from '../../../icon/icon.component';
import { CALL_STATE, CALL_TYPE } from '../../constants';
import { CallStoreService } from '../../services/call-store.service';
import { CallZegoService } from '../../services/call-zego.service';
import { PipService } from '../../services/pip.service';
import { CallShareScreenComponent } from '../call-share-screen/call-share-screen.component';
import { CallVideoItemComponent } from '../call-video-item/call-video-item.component';

@Component({
  selector: 'app-call-video-layout',
  standalone: true,
  imports: [CallVideoItemComponent, CallShareScreenComponent, AppIconComponent],
  templateUrl: './call-video-layout.component.html',
  styleUrl: './call-video-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallVideoLayoutComponent implements AfterViewInit {
  isFullscreen = input<boolean>(false);

  callStore = inject(CallStoreService);
  private callZegoService = inject(CallZegoService);
  private zegoStreamService = inject(ZegoStreamService);
  private pipService = inject(PipService);

  CALL_STATE = CALL_STATE;
  CALL_TYPE = CALL_TYPE;
  userInfo = getUserInfo();

  stopShareEvt = output<void>();

  readonly currentCall = this.callStore.currentCall;
  readonly callType = this.callStore.callType;
  readonly callState = this.callStore.callState;
  readonly participants = this.callStore.participants;
  readonly isShowMyNetworkWarning = this.callStore.isShowMyNetworkWarning;
  readonly isShowMyNetworkReconnecting = this.callStore.isShowMyNetworkReconnecting;
  readonly isShowRemoteNetworkWarning = this.callStore.isShowRemoteNetworkWarning;
  readonly localStream = this.callStore.localStream;
  readonly localMicState = this.callStore.localMicState;
  readonly localCamState = this.callStore.localCamState;
  readonly localSoundLevel = this.callStore.localSoundLevel;
  readonly isScreenSharing = this.callStore.isScreenSharing;
  readonly isRemoteSharing = this.callStore.isRemoteSharing;
  readonly isAnyScreenSharing = this.callStore.isAnyScreenSharing;
  readonly callDuration = this.callStore.callDurationText;
  readonly isOpenPip = this.pipService.isOpen;
  readonly remoteSoundLevels = this.callStore.remoteSoundLevels;

  isDevice = computed(() => isDevice(this.participants()[0]?.userId));
  isFullScreenShare = signal<boolean>(false);

  participant = computed(() => {
    if (this.participants().length === 0) return null;
    return this.participants()[0];
  });

  readonly sharingMicOn = computed(() =>
    this.isScreenSharing() ? this.localMicState() : (this.participant()?.isMicOn ?? false),
  );

  readonly sharingSoundLevel = computed(() => {
    if (this.isScreenSharing()) return this.localSoundLevel();
    const p = this.participant();
    if (!p) return 0;
    return this.remoteSoundLevels()[p.userId] ?? 0;
  });

  readonly localVideoData = computed(() => ({
    isSpeaking: this.localSoundLevel() > 10,
    isShareScreen: this.isScreenSharing(),
    micOn: this.localMicState(),
    camOn: this.localCamState(),
    videoId: 'local-video',
    id: this.userInfo.id,
    avatar: this.userInfo.imageUrl,
    name: this.userInfo.username,
    volume: this.localSoundLevel(),
    label: 'Bạn',
    device: false,
  }));

  readonly remoteVideoData = computed(() => {
    const p = this.participant();
    const userId = p?.userId ?? '';
    return {
      isSpeaking: (this.remoteSoundLevels()[userId] ?? 0) > 10,
      isShareScreen: this.isRemoteSharing(),
      micOn: p?.isMicOn ?? false,
      camOn: p?.isCamOn ?? false,
      videoId: 'remote-stream',
      id: userId,
      avatar: p?.avatar ?? '',
      name: p?.userName ?? '',
      volume: this.remoteSoundLevels()[userId] ?? 0,
      label: p?.userName ?? '',
      device: this.isDevice(),
    };
  });

  readonly networkQualityInfoName = this.callStore.networkQualityInfoName;

  constructor() {
    effect(
      () => {
        if (!this.isAnyScreenSharing()) {
          this.isFullScreenShare.set(false);
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      if (this.localStream() === null) return;
      if (this.localStream()!.getVideoTracks().length > 0) {
        let localVidElem = document.getElementById('local-video') as HTMLElement;
        if (!localVidElem && window.documentPictureInPicture?.window) {
          localVidElem = window.documentPictureInPicture.window.document.getElementById(
            'local-video',
          ) as HTMLElement;
        }
        if (localVidElem && !localVidElem.querySelector('video')) {
          this.localStream()!.playVideo(localVidElem, {
            enableAutoplayDialog: true,
            objectFit: 'cover',
          });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    const remoteId = this.participant()?.userId;
    if (remoteId) {
      const remoteStream = this.callZegoService.remoteStreams()[remoteId];
      if (remoteStream) {
        this.zegoStreamService.playStreamToDOM(remoteStream, 'remote-stream');
      }
    }
  }
}
